import React from 'react';
import { Eye, EyeOff, Save, X } from 'lucide-react';

const LABEL = 'text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold block mb-1';
const INPUT = 'w-full border border-earth/15 rounded px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-saffron/30';

const TEXT_FIELDS = [
  { key: 'meta_title', label: 'Meta Title', placeholder: 'Indian Spice Exporter to Japan | ASJK Laxmi Global', testId: 'lp-meta-title' },
  { key: 'meta_description', label: 'Meta Description', rows: 2, testId: 'lp-meta-description' },
  { key: 'keywords', label: 'Keywords', placeholder: 'spice exporter Japan, Indian spices Tokyo', testId: 'lp-keywords' },
  { key: 'h1', label: 'Page Heading (H1)', placeholder: 'Premium Indian Spices for Japan', testId: 'lp-h1' },
  { key: 'intro', label: 'Introduction', rows: 2, testId: 'lp-intro' },
];

const toSlug = (value) => value.toLowerCase().replace(/[^a-z0-9-]/g, '-');

function Field({ field, value, onChange }) {
  return (
    <div>
      <label className={LABEL}>{field.label}</label>
      {field.rows ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={field.rows} className={`${INPUT} resize-none`} data-testid={field.testId} />
      ) : (
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={field.placeholder} className={INPUT} data-testid={field.testId} />
      )}
    </div>
  );
}

function ProductPicker({ products, selected, onToggle }) {
  return (
    <div>
      <label className={`${LABEL} mb-2`}>Featured Products</label>
      <div className="flex flex-wrap gap-2">
        {products.map(p => (
          <button key={p.id} onClick={() => onToggle(p.id)} className={`px-3 py-1.5 rounded-md text-xs font-sans font-medium transition-colors ${selected.includes(p.id) ? 'bg-saffron text-white' : 'bg-cream/50 text-earth/60 hover:bg-saffron/10'}`} data-testid={`lp-product-${p.id}`}>
            {p.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function BasicsRow({ form, isNew, setField }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <label className={LABEL}>URL Slug</label>
        <input value={form.slug} onChange={e => setField('slug', toSlug(e.target.value))} placeholder="spice-exporter-japan" className={INPUT} disabled={!isNew} data-testid="lp-slug" />
        <p className="text-[9px] text-earth/30 font-sans mt-0.5">/market/{form.slug || '...'}</p>
      </div>
      <Field field={{ label: 'Country', placeholder: 'Japan', testId: 'lp-country' }} value={form.country} onChange={v => setField('country', v)} />
      <Field field={{ label: 'CTA Button Text', testId: 'lp-cta-text' }} value={form.cta_text} onChange={v => setField('cta_text', v)} />
    </div>
  );
}

export default function LandingPageEditor({ form, setForm, isNew, title, products, saving, onSave, onCancel }) {
  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const toggleProduct = (id) => setForm(f => ({ ...f, products: f.products.includes(id) ? f.products.filter(p => p !== id) : [...f.products, id] }));
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-6 space-y-4" data-testid="landing-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-earth-dark">{title}</h3>
        <button onClick={onCancel} className="text-earth/30 hover:text-earth" data-testid="lp-cancel"><X size={18} /></button>
      </div>
      <BasicsRow form={form} isNew={isNew} setField={setField} />
      {TEXT_FIELDS.map(f => <Field key={f.key} field={f} value={form[f.key]} onChange={v => setField(f.key, v)} />)}
      <div>
        <label className={LABEL}>Content (HTML)</label>
        <textarea value={form.content} onChange={e => setField('content', e.target.value)} rows={6} className="w-full border border-earth/15 rounded px-3 py-2 text-xs font-mono text-earth-dark focus:outline-none focus:ring-2 focus:ring-saffron/30 resize-y" data-testid="lp-content" />
      </div>
      <ProductPicker products={products} selected={form.products} onToggle={toggleProduct} />
      <label className="flex items-center gap-2 text-sm font-sans text-earth/60 cursor-pointer">
        <input type="checkbox" checked={form.published} onChange={e => setField('published', e.target.checked)} className="accent-saffron" data-testid="lp-published" />
        {form.published ? <Eye size={13} /> : <EyeOff size={13} />} {form.published ? 'Published' : 'Draft'}
      </label>
      <button onClick={onSave} disabled={saving || !form.slug || !form.country} className="w-full bg-saffron text-white font-sans font-semibold text-sm py-3 rounded-md hover:bg-saffron-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-landing">
        <Save size={14} /> {saving ? 'Saving...' : 'Save Landing Page'}
      </button>
    </div>
  );
}
